import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Form, notification, Row, Col, Button, Modal, Typography, Upload, Spin } from 'antd';
import config from '../../../config';
import * as userActions from '../../../redux/actions/userActions';
import {
  confirmationModal,
  formItemLayout,
  tailFormItemLayout,
  showFilesList,
  showUploadFileList,
  unexpectedError,
  beforeUpload,
} from '../../../common_functions';
import UploadTrigger from '../../../common_functions/UploadTrigger';
import UploadedCheck from '../../../common_functions/UploadedCheck';

const Glossmeter = props => {
  const dispatch = useDispatch();
  const formRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [glossmeterData, setGlossmeterData] = useState(false);
  const [title, setTitle] = useState('Add Photospectrometer');
  const [backTitle, backSetTitle] = useState('Add Micrometer');
  const [printData, setPrintData] = useState(false);
  const [filesState, setFilesState] = useState({ glossmeter: [] });
  let error = [];
  let saveAndNext = '';

  /**Handle use effect**/
  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        const { data } = await dispatch(
          userActions.getItem('print', props.print, {
            fields: [
              'c_print.acquisition,c_print.title,photo_spectro_meter.id,glossmeter.id,glossmeter.glossmeter.id,glossmeter.glossmeter.filename_download,collection_id.name,micrometer.id',
            ],
          })
        );
        setPrintData({
          acquisition: data.c_print[0]['acquisition'],
          title: data.c_print[0]['title'],
          name: data.collection_id.name,
        });
        const glossmeter = data?.glossmeter?.[0];

        if (glossmeter) {
          setFilesState({ glossmeter: showUploadFileList(glossmeter?.glossmeter?.id, glossmeter?.glossmeter?.filename_download) });
          glossmeter.glossmeter = glossmeter?.glossmeter?.id;
          formRef.current?.setFieldsValue(glossmeter);
          setGlossmeterData(glossmeter);
        }
        if (data?.photo_spectro_meter?.[0]) {
          setTitle('Edit Photospectrometer');
        }
        if (data?.micrometer?.[0]) {
          backSetTitle('Edit Micrometer');
        }
      } catch (error) {
        unexpectedError(error);
      } finally {
        setLoading(false);
      }
    }
    if (props.activeTab === 'glossmeter') {
      formRef.current?.setFields([
        {
          name: 'glossmeter',
          errors: [],
        },
      ]);
      fetchData();
    }
  }, [props.activeTab]);

  const complete = async values => {
    Modal.destroyAll();
    values = { ...values, object_status: 1 };
    await onSave(values);
  };
  const incomplete = async values => {
    Modal.destroyAll();
    values = { ...values, object_status: 0 };
    await onSave(values);
  };
  let onFinish = async (values, action = null) => {
    saveAndNext = '';
    if (!!action) {
      saveAndNext = action;
    }
    await confirmationModal(values, 'Status', 'Is this record complete or incomplete?', 'Incomplete', 'Complete', complete, incomplete);
  };

  /***Handle form submit**/
  let onSave = async values => {
    if (error.length) return false;
    setLoading(true);
    const formData = new FormData();
    Object.entries(values).forEach(([key, value]) => {
      key === 'object_status' && formData.append('object_status', value);
      if (value?.fileList?.length > 0) {
        formData.append(key, value.fileList[0].originFileObj);
      }
    });
    await dispatch(userActions.saveFileItem('glossmeter/glossmeter', formData, props.print, glossmeterData)).then(
      response => {
        setLoading(false);
        if (response) {
          formRef.current.resetFields('');
          formRef.current.setFieldsValue(response.data);
          setGlossmeterData(response.data);
          notification['success']({
            message: 'Glossmeter saved successfully!',
            description: '',
          });
          if (saveAndNext == '') {
            setTimeout(() => props.history.push('/print-objects', 1500));
          } else if (saveAndNext == 'next') {
            props.onChange('', 'photospectrometer', title, 'Edit Glossmeter');
          }
        }
      },
      errorReponse => {
        setLoading(false);
        if (!!errorReponse.errors) {
          formRef.current.setFields([
            {
              name: 'glossmeter',
              errors: [errorReponse.errors],
            },
          ]);
        } else {
          setLoading(false);
          unexpectedError();
        }
      }
    );
  };

  /**Handle form failed**/
  let onFinishFailed = ({ errorFields }) => {
    formRef.current.scrollToField(errorFields[0].name);
  };
  let backButton = () => {
    props.onChange('', 'micrometer', backTitle);
  };
  let fileChange = ({ file, fileList: newFileList }) => {
    try {
      error = [];
      const correctFileNames = [`${printData.acquisition}.gloss.xls`, `${printData.acquisition}.gloss.xlsx`];
      if (!(file instanceof File)) return;
      const fileNameLower = file.name.toLowerCase();
      if (!correctFileNames.some(name => name.toLowerCase() === fileNameLower)) {
        error.push(`Please upload a file with the name ${correctFileNames.join(' or ')}`);
      } else {
        setFilesState(prevState => ({
          ...prevState,
          glossmeter: newFileList,
        }));
      }
      formRef.current.setFields([
        {
          name: 'glossmeter',
          errors: error.length ? error : '',
        },
      ]);
    } catch (error) {
      console.log(error);
    }
  };
  const onRemove = file => {
    formRef.current.setFields([
      {
        name: 'glossmeter',
        errors: '',
      },
    ]);
    return new Promise((resolve, reject) => {
      resolve(true);
    });
  };

  const handleSaveAndNext = action => {
    formRef.current.validateFields().then(values => {
      onFinish(values, action);
    });
  };

  return (
    <>
      <Spin spinning={loading} size="large">
        <Row>
          <Col className="white-paper">
            <Row
              style={{
                backgroundColor: '#f5f5f5',
                border: '1px solid #0000000f',
                borderRadius: '7px',
                padding: '12px',
                marginBottom: '20px',
              }}
            >
              <Col md={8}>
                <div>Collection</div>
                <Typography.Title level={4} style={{ margin: 0 }}>
                  <div style={{ fontSize: '19px' }}>{printData.name}</div>
                </Typography.Title>
              </Col>
              <Col md={8}>
                <div>Collection Acquisition ID</div>
                <Typography.Title level={4} style={{ margin: 0 }}>
                  <div style={{ fontSize: '19px' }}>{printData.acquisition}</div>
                </Typography.Title>
              </Col>
              <Col md={8}>
                <div>Collection Title</div>
                <Typography.Title level={4} style={{ margin: 0 }}>
                  <div style={{ fontSize: '19px' }}>{printData.title}</div>
                </Typography.Title>
              </Col>
            </Row>
            <Form
              ref={formRef}
              layout="vertical"
              {...formItemLayout}
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
              initialValues={{ layout: tailFormItemLayout }}
            >
              <Row>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item
                    name="glossmeter"
                    label={
                      <div>
                        Glossmeter File
                        <UploadedCheck uploaded={glossmeterData?.glossmeter} />
                      </div>
                    }
                  >
                    <Upload
                      name="file"
                      className="create_object"
                      fileList={filesState.glossmeter}
                      accept={config.FileType.onlyXls.toString()}
                      beforeUpload={file => {
                        return beforeUpload(file, 'onlyXls', `${printData.acquisition}.gloss`);
                      }}
                      onRemove={onRemove}
                      onChange={fileChange}
                      multiple={false}
                      maxCount={1}
                      showUploadList={showFilesList(glossmeterData?.glossmeter, 'glossmeter')}
                    >
                      <UploadTrigger fileType="onlyXls" />
                    </Upload>
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item {...tailFormItemLayout}>
                <Button
                  type="default"
                  htmlType="button"
                  onClick={backButton}
                  style={{
                    borderColor: '#d9d9d9',
                    background: '#f5f5f5',
                    color: 'rgba(0, 0, 0, 0.85)',
                  }}
                >
                  Back
                </Button>
                {props.showButton()}
                {props.showButton() != '' ? (
                  <Button className="save-btn" type="primary" style={{ margin: '0 8px' }} onClick={() => handleSaveAndNext('next')}>
                    Save and Next
                  </Button>
                ) : (
                  ''
                )}
              </Form.Item>
            </Form>
          </Col>
        </Row>
      </Spin>
    </>
  );
};
export default Glossmeter;
